"use client"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import PortableTextRenderer from "@/app/(site)/_components/Atoms/PortableTextRenderer/PortableTextRenderer"
import { getGeneralLinkHref } from "@/app/(site)/_utils/misc"
import { ImageType, TitledGeneralLink } from "@/types"
import { ColorValue } from "@sanity/color-input"
import { PortableTextBlock } from "sanity"
import BaseImage from "../../Atoms/BaseImage"
import StoryText from "../../Atoms/Storytext"
import { Content } from "../../ContentMapper"
import DynamicLink from "../../Molecules/Link"

export interface FeatureBlockData extends Content {
    heading: string
    portableTextParagraph?: PortableTextBlock[]
    links: TitledGeneralLink[]
    image: ImageType
    hasWhiteText: boolean
    backgroundColor: ColorValue
}

type FeatureProps = {
    data?: FeatureBlockData
    isAboveTheFold?: boolean
}
export default function Feature({ data, isAboveTheFold }: FeatureProps) {
    if (!data) return null
    const { heading, image, links, hasWhiteText, backgroundColor } = data
    const textColorClass = hasWhiteText ? "text-white" : "text-main-black"
    return (
        <section
            className={`overflow-hidden text-main-black md:h-[calc(100vh-150px)] ${textColorClass}`}
            style={{
                backgroundColor: backgroundColor?.hex
            }}
        >
            <div className="grid h-full grid-cols-1 gap-24 p-16 md:grid-flow-row-dense md:grid-cols-2 md:p-24">
                <div className="col-span-1 flex h-full flex-col justify-between">
                    <div className="grid grid-cols-2 gap-24">
                        <StoryText size="size1">{heading}</StoryText>
                        <div className={`hidden md:mb-0 md:block ${textColorClass}`}>
                            {" "}
                            {!!data?.portableTextParagraph && (
                                <PortableTextRenderer
                                    content={data?.portableTextParagraph}
                                    render={{
                                        block: {
                                            normal: text => (
                                                <StoryText size="size4">{text}</StoryText>
                                            )
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    <div className="col-span-2">
                        <div
                            className={`mb-16 uppercase leading-[90%] tracking-[-8.8px] ${textColorClass}`}
                        ></div>
                        <div className={`mb-16 block md:hidden ${textColorClass}`}>
                            <div className="mb-158 md:mb-0">
                                {!!data?.portableTextParagraph && (
                                    <PortableTextRenderer
                                        content={data?.portableTextParagraph}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={`flex gap-24 uppercase md:flex ${textColorClass}`}>
                        {links?.map((link, index) => (
                            <DynamicLink
                                key={index}
                                href={getGeneralLinkHref(link) ?? ""}
                                prefetch={false}
                            >
                                <div
                                    key={index}
                                    className="flex items-center gap-8 font-trimRegular"
                                >
                                    {link.title}
                                    <IconArrowRight className="h-20 w-20" />
                                </div>
                            </DynamicLink>
                        ))}
                    </div>
                </div>
                <div
                    style={{ position: "relative" }}
                    className="relative order-first col-span-1 mb-0 block aspect-square w-full md:order-last md:m-0 md:mb-0 md:aspect-auto"
                >
                    <BaseImage
                        src={image?.asset._ref || ""}
                        alt={image?.alt || ""}
                        fill
                        mimeType={image?.asset?.mimeType || ""}
                        priority={isAboveTheFold}
                        loading={isAboveTheFold ? "eager" : "lazy"}
                        className="object-cover"
                        sizes="(max-width: 968px) 100vw, 50vw"
                    />
                </div>
            </div>
        </section>
    )
}
